import { ApiError, type Me, type SiteContext } from '@smarton-pbx/shared';
import {
  Building2,
  ChartColumn,
  Handshake,
  Headset,
  PhoneCall,
  TrendingUp,
  History,
  Lightbulb,
  ListChecks,
  MapPin,
  Phone as PhoneIcon,
  ScrollText,
  SlidersHorizontal,
  Users,
  Voicemail as VoicemailIcon,
  MessageSquare,
  Workflow,
  BookUser,
  SquareKanban,
} from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { api, photoUrl } from '../api';
import { showLanguage, t, tc } from '../i18n';
import { AuthLayout } from '../auth/AuthLayout';
import { SetPassword } from '../auth/SetPassword';
import { SignIn } from '../auth/SignIn';
import { AppShell, BrandMark, type NavItem } from '../components/AppShell';
import { Loading, Notice } from '../components/ui';
import { logoUrl } from '../lib/branding';
import { tenantHelp } from '../lib/help';
import { registerServiceWorker } from '../lib/push';
import { Account } from './Account';
import { AuditLog } from './AuditLog';
import { Branding } from './Branding';
import { CrmSettings } from './CrmSettings';
import { CallPage } from './CallPage';
import { CallSettings } from './CallSettings';
import { Calls } from './Calls';
import { Carriers } from './Carriers';
import { Dashboard } from './Dashboard';
import { DigestPage, Insights } from './Insights';
import { Numbers } from './Numbers';
import { AgentStatusMenu, CallbacksPage, QueuesLivePage, QueuesPage } from './queues';
import { FlowsPage } from './flows/FlowsPage';
import { CallerListsPage, HoldMusicPage, OpeningHoursPage, PromptsPage } from './flows/parts';
import { ContactPage, ContactsPage } from './contacts';
import { PipelinePage } from './pipeline/PipelinePage';

// The canvas (React Flow) loads when a flow is opened.
const FlowEditorPage = lazy(() => import('./flows/FlowEditor').then((m) => ({ default: m.FlowEditorPage })));
import { People, roleNames } from './People';
import { Phone } from './Phone';
import { Regions } from './Regions';
import { PhoneSetupDialog, usePhoneSetup } from './PhoneSetup';
import { NotificationBell, NotificationsProvider } from './notifications';
import { CallCard, PhoneStatusChip, SoftphoneProvider } from './softphone';
import { TextsBadge, TextsPage } from './Texts';
import { Voicemail } from './Voicemail';

type TenantContext = Extract<SiteContext, { kind: 'tenant' }>;

export function TenantApp({ context, onBrandingChanged }: { context: TenantContext; onBrandingChanged: () => void }) {
  const [me, setMe] = useState<Me | null>();
  const { branding } = context;
  const logo = branding.logoVersion ? logoUrl(branding.logoVersion) : null;

  const loadMe = () =>
    api.me().then((loaded) => {
      // Chosen elsewhere (another browser, the phone app): shown here too.
      if (loaded.language) showLanguage(loaded.language);
      setMe(loaded);
    }, (error: unknown) => {
      if (error instanceof ApiError && error.status === 401) setMe(null);
      else throw error;
    });

  useEffect(() => {
    void loadMe();
  }, []);

  if (context.suspended) {
    return (
      <AuthLayout name={branding.productName} logoUrl={logo}>
        <div className="auth-title">
          <h1>{t('Account suspended')}</h1>
        </div>
        <Notice>{t('This account is suspended. Contact your provider.')}</Notice>
      </AuthLayout>
    );
  }

  return (
    <Routes>
      <Route path="/set-password" element={<SetPassword name={branding.productName} logoUrl={logo} setPassword={api.setPassword} />} />
      <Route
        path="*"
        element={
          me === undefined ? (
            <Loading />
          ) : me === null ? (
            <SignIn
              name={branding.productName}
              logoUrl={logo}
              signIn={api.signIn}
              verify={api.verifyMfa}
              onSignedIn={() => void loadMe()}
            />
          ) : (
            <SoftphoneProvider
              userId={me.id}
              productName={branding.productName}
              iconUrl={branding.faviconVersion ? `/api/branding/favicon?v=${branding.faviconVersion}` : logo}
              crmUrl={me.crmUrl}
            >
              <Signedin me={me} onMeChanged={() => void loadMe()} productName={branding.productName} logo={logo} docsUrl={context.docsUrl} onBrandingChanged={onBrandingChanged} />
            </SoftphoneProvider>
          )
        }
      />
    </Routes>
  );
}

function Signedin({
  me,
  onMeChanged,
  productName,
  logo,
  docsUrl,
  onBrandingChanged,
}: {
  me: Me;
  onMeChanged: () => void;
  productName: string;
  logo: string | null;
  docsUrl: string | null;
  onBrandingChanged: () => void;
}) {
  const isAdmin = me.role === 'TENANT_ADMIN';
  const canSeePeople = isAdmin || me.role === 'REGION_MANAGER';
  const setup = usePhoneSetup();

  // Shows notifications while the app is closed (Web Push).
  useEffect(() => registerServiceWorker(), []);

  // The pages, in groups that open: what people call, their customers, the reports, and running the company's system.
  const nav: NavItem[] = [
    { to: '/', label: t('Phone'), icon: PhoneIcon, end: true },
    {
      id: 'calls',
      label: t('Calls'),
      icon: PhoneCall,
      badge: <TextsBadge />,
      items: [
        { to: '/calls', label: t('Call history'), short: t('Calls'), icon: History },
        { to: '/voicemail', label: t('Voicemail'), icon: VoicemailIcon },
        { to: '/texts', label: t('Texts'), icon: MessageSquare, badge: <TextsBadge /> },
        // Managers set queues up (and reach the live view from there); others watch theirs.
        ...(canSeePeople ? [{ to: '/queues', label: t('Queues'), icon: Headset }] : me.hasQueues ? [{ to: '/queues/live', label: t('Queues'), icon: Headset }] : []),
        ...(me.hasQueues ? [{ to: '/callbacks', label: t('Call back'), icon: ListChecks }] : []),
      ],
    },
    {
      id: 'customers',
      label: t('Customers'),
      icon: Handshake,
      items: [
        { to: '/contacts', label: t('Contacts'), icon: BookUser },
        { to: '/pipeline', label: t('Pipeline'), icon: SquareKanban },
      ],
    },
    {
      id: 'reports',
      label: t('Reports'),
      icon: TrendingUp,
      items: [
        { to: '/dashboard', label: t('Dashboard'), icon: ChartColumn },
        { to: '/insights', label: t('Insights'), icon: Lightbulb },
      ],
    },
    ...(canSeePeople
      ? [
          {
            id: 'admin',
            label: tc('menu', 'Admin'),
            icon: Building2,
            items: [
              { to: '/people', label: t('People'), icon: Users },
              { to: '/regions', label: t('Regions'), icon: MapPin },
              { to: '/flows', label: t('Call flows'), icon: Workflow },
              // Settings holds the carriers, numbers and branding; region managers see only the numbers there.
              isAdmin ? { to: '/settings', label: t('Settings'), icon: SlidersHorizontal } : { to: '/settings/numbers', label: t('Settings'), icon: SlidersHorizontal },
              ...(isAdmin ? [{ to: '/audit', label: t('Audit log'), icon: ScrollText }] : []),
            ],
          },
        ]
      : []),
  ];

  async function signOut() {
    await api.signOut();
    window.location.assign('/');
  }

  return (
    <NotificationsProvider>
      <AppShell
        brand={<BrandMark name={productName} logoUrl={logo} />}
        nav={nav}
        status={
          <div className="row status-row">
            <PhoneStatusChip />
            <AgentStatusMenu up />
          </div>
        }
        actions={
          <>
            <AgentStatusMenu />
            <NotificationBell />
          </>
        }
        help={{ docsUrl, pages: tenantHelp }}
        user={{
          name: me.name,
          detail: t('{role} · ext. {extension}', { role: roleNames[me.role], extension: me.extension }),
          photo: photoUrl(me.id, me.photoVersion),
        }}
        accountPath="/account"
        saveLanguage={api.setLanguage}
        onSignOut={() => void signOut()}
      >
        <Routes>
          <Route path="/" element={<Phone me={me} setup={setup} />} />
          <Route path="/calls" element={<Calls me={me} />} />
          <Route path="/calls/:id" element={<CallPage me={me} />} />
          <Route path="/voicemail" element={<Voicemail me={me} />} />
          <Route path="/texts" element={<TextsPage me={me} />} />
          <Route path="/texts/:id" element={<TextsPage me={me} />} />
          <Route path="/contacts" element={<ContactsPage me={me} />} />
          <Route path="/contacts/:id" element={<ContactPage me={me} />} />
          <Route path="/pipeline" element={<PipelinePage me={me} />} />
          <Route path="/queues/live" element={<QueuesLivePage canSetUp={canSeePeople} />} />
          <Route path="/callbacks" element={<CallbacksPage me={me} />} />
          {canSeePeople && <Route path="/queues" element={<QueuesPage me={me} />} />}
          {canSeePeople && <Route path="/flows" element={<FlowsPage me={me} />} />}
          {canSeePeople && (
            <Route
              path="/flows/:id"
              element={
                <Suspense fallback={<Loading />}>
                  <FlowEditorPage />
                </Suspense>
              }
            />
          )}
          {canSeePeople && <Route path="/prompts" element={<PromptsPage me={me} />} />}
          {canSeePeople && <Route path="/hold-music" element={<HoldMusicPage me={me} />} />}
          {canSeePeople && <Route path="/opening-hours" element={<OpeningHoursPage me={me} />} />}
          {canSeePeople && <Route path="/caller-lists" element={<CallerListsPage me={me} />} />}
          <Route path="/dashboard" element={<Dashboard me={me} />} />
          <Route path="/insights" element={<Insights me={me} />} />
          <Route path="/insights/digests/:id" element={<DigestPage />} />
          {canSeePeople && <Route path="/people" element={<People me={me} />} />}
          {canSeePeople && <Route path="/regions" element={<Regions me={me} />} />}
          {canSeePeople && <Route path="/settings/numbers" element={<Numbers me={me} />} />}
          {isAdmin && <Route path="/settings/carriers" element={<Carriers />} />}
          {/* Where these pages used to be. */}
          {canSeePeople && <Route path="/numbers" element={<Navigate to="/settings/numbers" replace />} />}
          {isAdmin && <Route path="/carriers" element={<Navigate to="/settings/carriers" replace />} />}
          {isAdmin && <Route path="/settings" element={<CallSettings />} />}
          {isAdmin && <Route path="/settings/branding" element={<Branding onChanged={onBrandingChanged} />} />}
          {isAdmin && <Route path="/settings/smarton" element={<CrmSettings />} />}
          {isAdmin && <Route path="/branding" element={<Navigate to="/settings/branding" replace />} />}
          {isAdmin && <Route path="/audit" element={<AuditLog />} />}
          <Route path="/account" element={<Account me={me} onChanged={onMeChanged} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CallCard me={me} />
        <PhoneSetupDialog open={setup.open} onClose={setup.close} />
      </AppShell>
    </NotificationsProvider>
  );
}
